
import Vue from "vue";
import { mapState, mapMutations, mapActions } from "vuex";
import UpdateMultipleItems from "../components/UpdateMultipleItems";
import EditDate from "../components/EditDate";
import SimpleModal from "@/components/Dialogs/SimpleModal";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle";
import ColumnManager from "@/components/shared/ColumnManager.vue";
import TruncatedTextCell from "@/components/shared/TruncatedTextCell";
import EntityIdLink from "@/components/shared/EntityIdLink";
import { updateHourlyRate, updateTable, uncompleteJob, getOffersByJobId, adminAcceptOffer, adminDeclineOffer, adminRequestResubmit, cancelJobHygienist, cancelJobDentist, cancelJobAgent, getPaginateItems, getJobFraudRuleOverrides, setJobFraudRuleOverride, updateJobPaymentMethod } from "@/api";
import { headersInterface } from "@/interfaces/Tables.interfaces";
import { paginateTable } from "@/interfaces/Additional.interfaces";
import { startCase } from "lodash";
import {
  formatJobPaymentMethod,
  isPaymentMethodEditable,
  paymentMethodOpts
} from "@/helpers/jobPaymentMethod";
import { syncJobSearchFromRoute } from "@/helpers/jobLinkedSearch";
import { differenceInDays, format } from "date-fns";
import { formatInTimeZone, utcToZonedTime } from "date-fns-tz";
import { constants } from "@/utils/constants";
import { currentTimeZone } from "@/helpers";
import { getDataTables } from "@/mixins/getDataTables";
import {
  formatJobStatus as formatSharedJobStatus,
  formatJobType,
  formatOfferStatus as formatSharedOfferStatus,
  JOB_STATUS_COLORS,
  OFFER_STATUS_COLORS
} from "@/helpers/adminEnumLabels";

interface Item {
  id: number;
  status: number;
  startDate: any;
}

export default Vue.extend({
  name: "Job",
  components: {
    UpdateMultipleItems,
    EditDate,
    SimpleModal,
    TempStarsTableTitle,
    ColumnManager,
    TruncatedTextCell,
    EntityIdLink
  },
  data: () => {
    return {
      tableSearch: "",
      currentItem: {} as Item,
      modalSubmit: () => {},
      showModal: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      } as any,
      initialized: false,
      hourlyRateDialog: false,
      currentHourlyRate: 0,
      managedHeaders: [] as any[],
      baseJobHeaders: [
        { text: "ID", value: "id", align: "left" },
        { text: "Dentist ID", value: "dentistId", align: "left" },
        { text: "Location ID", value: "locationId", align: "left" },
        { text: "Offers", value: "offersCount", align: "center" },
        { text: "Hygienist ID", value: "hygienistId", align: "left" },
        { text: "Booked On", value: "bookedOn", align: "left" },
        { text: "Hourly Rate", value: "hourlyRate", align: "left" },
        { text: "Status", value: "status", align: "left" },
        { text: "Autobook", value: "Autobook", align: "center" },
        { text: "Instabook", value: "Instabook", align: "center" },
        { text: "Fraud Detection Timestamp", value: "fraudDetectionTimestamp", align: "left" },
        { text: "Fraud Detection Reason", value: "fraudDetectionReason", align: "left" },
        { text: "Dentist Rating", value: "dentistRating", align: "left" },
        { text: "Hygienist Rating", value: "hygienistRating", align: "left" },
        { text: "Dentist Billed", value: "dentistBilled", align: "left" },
        { text: "Start Date", value: "startDate", align: "left" },
        // T2-6234: original vs. current shift hours
        { text: "Original Times", value: "originalTimes", align: "left", sortable: false },
        { text: "Current Times", value: "currentTimes", align: "left", sortable: false },
        { text: "Actual Start", value: "actualStart", align: "left" },
        { text: "Actual End", value: "actualEnd", align: "left" },
        { text: "Dentist Private Notes", value: "dentistPrivateNotes", align: "left" },
        { text: "Hygienist Private Notes", value: "hygienistPrivateNotes", align: "left" },
        { text: "Type", value: "type", align: "left" },
        { text: "Required Level", value: "requiredLevel", align: "left" },
        { text: "Payment Terms", value: "paymentTerms", align: "left" },
        { text: "Payment Method", value: "paymentMethod", align: "left" },
        { text: "First Booked", value: "firstBooked", align: "left" },
        { text: "Booked By", value: "bookedBy", align: "left" },
        { text: "Actions", value: "actions", align: "center", sortable: false }
      ],
      // T2-6868
      editingPaymentMethodJobId: null as number | null,
      editingPaymentMethodValue: null as number | null,
      savingPaymentMethodJobId: null as number | null,
      offersDialog: false,
      offersData: [] as any[],
      offersLoading: false,
      selectedJobId: null as number | null,
      acceptOfferDialog: false,
      offerToAccept: null as any,
      acceptingOfferId: null as number | null,
      declineOfferDialog: false,
      offerToDecline: null as any,
      decliningOfferId: null as number | null,
      declineReason: "" as string,
      requestResubmitDialog: false,
      offerToResubmit: null as any,
      resubmittingOfferId: null as number | null,
      showJobDetailDialog: false,
      selectedJob: null as any,
      shiftData: [] as any[],
      shiftLoading: false,
      cancelling: {
        byTemp: false,
        byDO: false,
        byAgent: false
      },
      cancelDialog: {
        show: false,
        type: "",
        note: "",
        color: "",
        loadingKey: "" as "byTemp" | "byDO" | "byAgent",
        repostShift: false
      },
      fraudReasonDialog: false,
      fraudReasonItem: null as any,
      fraudCopySnackbar: false,
      // T2-6896
      unenforcedFraudRuleNames: [] as string[],
      fraudOverridesLoading: false,
      fraudOverrideSavingRule: null as string | null,
      LOCATION_VS_TALENT_CREATION_RULE: "Location vs Talent Creation"
    };
  },
  computed: {
    ...mapState({
      tablename: "tablename",
      headers: "tableHeaders",
      content: "tableContent"
    }),
    tableHeaders(): Array<headersInterface> {
      // Use managed headers if available (from ColumnManager)
      if ((this as any).managedHeaders && (this as any).managedHeaders.length > 0) {
        return (this as any).managedHeaders as Array<headersInterface>;
      }
      
      // Default headers
      const tableColumns = constants.enabledColumns.jobTable;
      const data: headersInterface[] = tableColumns.map(i => {
        return { align: "left", text: startCase(i), value: i };
      });
      const bookedByHeader: headersInterface = {
        align: "left",
        text: "Booked By",
        value: "bookedBy"
      };
      const actionsHeader = {
        align: "center",
        text: "Actions",
        value: "actions",
        sortable: false,
        width: "150px"
      };
      return [...data, bookedByHeader, actionsHeader] as Array<
        headersInterface
      >;
    },
    daysBeatweenDates(): number {
      return differenceInDays(new Date(this.currentItem.startDate), new Date());
    },
    trimmedTableSearch(): string {
      return typeof this.tableSearch === "string" ? this.tableSearch.trim() : "";
    },
    parsedFraudRules(): Array<Record<string, any>> {
      const raw =
        this.fraudReasonItem && this.fraudReasonItem.fraudDetectionReason;
      if (!raw) return [];
      try {
        const parsed = JSON.parse(String(raw));
        if (Array.isArray(parsed)) {
          return parsed.filter(
            r => r && typeof r === "object" && !Array.isArray(r)
          );
        }
        if (parsed && typeof parsed === "object") {
          return [parsed];
        }
        return [];
      } catch (e) {
        return [];
      }
    },
    triggeredFraudRulesCount(): number {
      return this.parsedFraudRules.filter(
        r => Number(r.score) > 0 && !this.isFraudRuleUnenforced(r)
      ).length;
    },
    offersHeaders(): Array<headersInterface> {
      return [
        { text: "Status", value: "status", align: "left" },
        { text: "Hygienist ID", value: "hygienistId", align: "left" },
        { text: "Hygienist Name", value: "hygienistName", align: "left" },
        { text: "Partial Offer?", value: "isPartialOffer", align: "center" },
        { text: "Hourly Rate", value: "hourlyRate", align: "left" },
        { text: "Views", value: "numViews", align: "center" },
        { text: "First View", value: "firstViewed", align: "left" },
        { text: "Last View", value: "lastViewed", align: "left" },
        { text: "Actions", value: "actions", align: "center", sortable: false }
      ];
    }
  },
  mixins: [getDataTables],
  created() {
    (this as any).applyLinkedJobIdFromRoute();
  },
  watch: {
    // T2-7038: Location Payments clicks another job while already on /tables/Job.
    // created() does not re-run, so search stayed on the previous id.
    "$route.query.jobId"() {
      (this as any).applyLinkedJobIdFromRoute();
    }
  },
  methods: {
    formatJobType,
    applyLinkedJobIdFromRoute() {
      syncJobSearchFromRoute(this as any);
    },
    ...mapActions(["getPaginateData"]),
    ...mapMutations(["modify", "callSnackbar"]),
    findHeaderIndex(name: string): number {
      return this.headers.findIndex(item => item.value === name);
    },
    formatJobStatus(status: number | null | undefined): string {
      return formatSharedJobStatus(status);
    },
    getJobStatusColor(status: number | null | undefined): string {
      if (status === null || status === undefined) return "#757575";
      return JOB_STATUS_COLORS[Number(status)] || "#757575";
    },
    getJobStatusIcon(status: number | null | undefined): string {
      if (status === null || status === undefined) return "mdi-help-circle";
      const iconMap: Record<number, string> = {
        1: "mdi-calendar-clock", // Posted
        2: "mdi-hand-wave", // Has Offers
        3: "mdi-check-circle", // Confirmed
        4: "mdi-check-circle-outline", // Completed
        5: "mdi-clock-alert-outline", // Offer Expired
        6: "mdi-alert-circle", // Expired
        7: "mdi-delete-circle" // Deleted
      };
      return iconMap[status] || "mdi-help-circle";
    },
    async changeStatusHandler() {
      try {
        // Use the new uncompleteJob API endpoint when changing from Completed (4) to Confirmed (3)
        // This ensures all counters and valueGives are properly reverted
        if (this.currentItem.status === 4) {
          await uncompleteJob(this.currentItem.id, "");
        } else {
          // For other status changes, use the direct update
          await updateTable({
            tablename: "Job",
            columnname: "status",
            id: this.currentItem.id,
            value: 3,
            prevValue: 4
          });
        }
        await this.loadData();

        this.callSnackbar({
          visible: true,
          text:
            this.currentItem.status === 4
              ? "Job marked as not completed successfully"
              : "Status updated successfully",
          color: "green darken-2"
        });

        this.showModal = false;
      } catch (error) {
        this.callSnackbar({
          visible: true,
          text:
            error instanceof Error
              ? error.message
              : "Error updating job status",
          color: "red darken-2"
        });
        this.$emit("error");
      }
    },

    formatDateTime(UTCdateTime: string) {
      return format(
        utcToZonedTime(UTCdateTime, currentTimeZone()),
        "MMM d yyyy, HH:mm"
      );
    },

    // T2-6234: compact "HH:mm – HH:mm" range for the Original/Current Times columns.
    formatTimeRange(startUtc: string, endUtc: string) {
      const tz = currentTimeZone();
      return `${format(utcToZonedTime(startUtc, tz), "HH:mm")} – ${format(
        utcToZonedTime(endUtc, tz),
        "HH:mm"
      )}`;
    },

    formatUTC(UTCdateTime: string) {
      return formatInTimeZone(UTCdateTime, "UTC", "MMM d, yyyy");
    },

    openFraudReason(item: any) {
      this.fraudReasonItem = item;
      this.fraudReasonDialog = true;
      this.unenforcedFraudRuleNames = this.extractUnenforcedFromReason(item);
      this.loadFraudRuleOverrides(item && item.id);
    },
    extractUnenforcedFromReason(item: any): string[] {
      if (!item || !item.fraudDetectionReason) return [];
      try {
        const parsed = JSON.parse(String(item.fraudDetectionReason));
        if (!Array.isArray(parsed)) return [];
        return parsed
          .filter(r => r && r.ignored === true && r.name)
          .map(r => String(r.name));
      } catch (e) {
        return [];
      }
    },
    async loadFraudRuleOverrides(jobId: number | null | undefined) {
      if (!jobId) return;
      this.fraudOverridesLoading = true;
      try {
        const res = await getJobFraudRuleOverrides(Number(jobId));
        this.unenforcedFraudRuleNames = Array.isArray(res.activeRuleNames)
          ? res.activeRuleNames
          : [];
      } catch (e) {
        // Keep any ignored flags from stored reason; surface soft error.
        this.callSnackbar({
          visible: true,
          text:
            e instanceof Error
              ? e.message
              : "Could not load fraud rule overrides",
          color: "orange darken-2"
        });
      } finally {
        this.fraudOverridesLoading = false;
      }
    },
    isFraudRuleUnenforced(rule: Record<string, any>): boolean {
      if (!rule || !rule.name) return false;
      if (rule.ignored === true) return true;
      return this.unenforcedFraudRuleNames.indexOf(String(rule.name)) !== -1;
    },
    displayFraudRuleName(rule: Record<string, any>): string {
      const name = (rule && rule.name) || "Unnamed rule";
      if (name === this.LOCATION_VS_TALENT_CREATION_RULE) {
        return "Location vs Talent Creation (signup timing)";
      }
      return name;
    },
    fraudRuleHelpText(rule: Record<string, any>): string {
      if (rule && (rule.detail || rule.details || rule.reason)) {
        return String(rule.detail || rule.details || rule.reason);
      }
      if (rule && rule.name === this.LOCATION_VS_TALENT_CREATION_RULE) {
        return "Signup-timing proximity between office and talent accounts (not map geography).";
      }
      if (this.isFraudRuleUnenforced(rule)) {
        const raw =
          rule.rawScore != null ? rule.rawScore : "previously scored";
        return `Un-enforced for this job — raw score ${raw} ignored for Pay Now.`;
      }
      return "";
    },
    canToggleFraudRule(rule: Record<string, any>): boolean {
      if (!rule || !rule.name) return false;
      // Show control when the rule contributes (or did before ignore), or is already un-enforced.
      const effective =
        rule.rawScore != null ? Number(rule.rawScore) : Number(rule.score);
      return this.isFraudRuleUnenforced(rule) || effective > 0;
    },
    fraudRuleIcon(rule: Record<string, any>): string {
      if (this.isFraudRuleUnenforced(rule)) return "mdi-cancel";
      return Number(rule.score) > 0
        ? "mdi-alert-circle"
        : "mdi-check-circle-outline";
    },
    fraudRuleIconColor(rule: Record<string, any>): string {
      if (this.isFraudRuleUnenforced(rule)) return "#0B4598";
      return Number(rule.score) > 0 ? "#FB7672" : "#13CA82";
    },
    fraudRuleScoreChipColor(rule: Record<string, any>): string {
      if (this.isFraudRuleUnenforced(rule)) return "#0B4598";
      return Number(rule.score) > 0 ? "#FB7672" : "#E0E0E0";
    },
    fraudRuleScoreChipTextColor(rule: Record<string, any>): string {
      if (this.isFraudRuleUnenforced(rule)) return "white";
      return Number(rule.score) > 0 ? "white" : "#555";
    },
    fraudRuleScoreLabel(rule: Record<string, any>): string {
      if (this.isFraudRuleUnenforced(rule)) {
        const raw =
          rule.rawScore != null ? rule.rawScore : "—";
        return `ignored (was ${raw})`;
      }
      return `score: ${rule.score ?? "—"}`;
    },
    async toggleFraudRuleEnforce(rule: Record<string, any>) {
      if (!this.fraudReasonItem || !rule || !rule.name) return;
      const jobId = Number(this.fraudReasonItem.id);
      const currentlyUnenforced = this.isFraudRuleUnenforced(rule);
      const nextUnenforced = !currentlyUnenforced;
      this.fraudOverrideSavingRule = String(rule.name);
      try {
        const res = await setJobFraudRuleOverride(
          jobId,
          String(rule.name),
          nextUnenforced
        );
        this.unenforcedFraudRuleNames = Array.isArray(res.activeRuleNames)
          ? res.activeRuleNames
          : [];
        if (Array.isArray(res.components)) {
          this.fraudReasonItem = {
            ...this.fraudReasonItem,
            fraudDetectionScore: res.total,
            fraudDetectionReason: JSON.stringify(res.components),
            fraudDetectionTimestamp: new Date().toISOString()
          };
          // Keep table row in sync if present in content
          const row = (this.content || []).find(
            (r: any) => Number(r.id) === jobId
          );
          if (row) {
            row.fraudDetectionScore = res.total;
            row.fraudDetectionReason = JSON.stringify(res.components);
          }
        }
        this.callSnackbar({
          visible: true,
          text: nextUnenforced
            ? `Un-enforced “${rule.name}” for job ${jobId}`
            : `Re-enforced “${rule.name}” for job ${jobId}`,
          color: "green darken-2"
        });
      } catch (e) {
        this.callSnackbar({
          visible: true,
          text:
            e instanceof Error
              ? e.message
              : "Failed to update fraud rule override",
          color: "red darken-2"
        });
      } finally {
        this.fraudOverrideSavingRule = null;
      }
    },
    async copyFraudReason() {
      const raw =
        this.fraudReasonItem && this.fraudReasonItem.fraudDetectionReason;
      if (!raw) return;
      const text = String(raw);
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      this.callSnackbar({
        visible: true,
        text: "Fraud detection JSON copied to clipboard",
        color: "green darken-2"
      });
    },

    formatDentistBilled(value: number | string | null | undefined): string {
      if (value === null || value === undefined || value === "") return "-";
      const num = Number(value);
      if (Number.isNaN(num)) return "-";
      return constants.dentistBilledLabels[num] || String(num);
    },

    bookedBy(item): string {
      if (!item.Instabook && !item.Autobook) {
        if (item.partialOfferId) {
          return "Submitted Offer";
        }
        return "Manually Booked";
      } else if (item.Instabook && !item.Autobook) {
        return "Instabooked by talent";
      } else if (!item.Instabook && item.Autobook) {
        return "Autobooked by office";
      } else {
        return "Instabooked/Autobooked";
      }
    },

    async loadData() {
      // Use the mixin's getDataFromApi for consistent pagination behavior
      await (this as any).getDataFromApi();
    },
    async handleRowClick(item: any) {
      this.selectedJob = item;
      this.showJobDetailDialog = true;
      await this.loadShiftData(item.id);
    },
    async loadShiftData(jobId: number) {
      this.shiftLoading = true;
      this.shiftData = [];
      try {
        // Use getPaginateItems directly to avoid affecting the store (which would change the job table)
        const shifts = await getPaginateItems({
          tableName: "Shift",
          limit: 100,
          page: 1,
          columnNames: constants.enabledColumns.shiftTable || [
            "id",
            "jobId",
            "shiftDate",
            "postedStart",
            "postedEnd",
            "actualStart",
            "actualEnd",
            "totalHours",
            "unpaidHours",
            "billableHours",
            "gapUnpaid",
            "type"
          ],
          search: jobId.toString(),
          searchColumns: ["jobId"] // Only use jobId for search - it's whitelisted in backend
        } as paginateTable);
        
        // Filter shifts by jobId (in case search returns multiple)
        this.shiftData = Array.isArray(shifts) 
          ? shifts.filter((shift: any) => Number(shift.jobId) === Number(jobId))
          : [];
      } catch (error) {
        this.callSnackbar({
          visible: true,
          text: error instanceof Error ? error.message : "Failed to load shift data",
          color: "red darken-2"
        });
      } finally {
        this.shiftLoading = false;
      }
    },
    handleCancelByTemp() {
      if (!this.selectedJob) return;
      this.cancelDialog = {
        show: true,
        type: "by Temp",
        note: "",
        color: "#FF9800",
        loadingKey: "byTemp",
        repostShift: false
      };
    },
    handleCancelByDO() {
      if (!this.selectedJob) return;
      this.cancelDialog = {
        show: true,
        type: "by Dental Office",
        note: "",
        color: "#F44336",
        loadingKey: "byDO",
        repostShift: false
      };
    },
    handleCancelByAgent() {
      if (!this.selectedJob) return;
      this.cancelDialog = {
        show: true,
        type: "by Agent",
        note: "",
        color: "#9C27B0",
        loadingKey: "byAgent",
        repostShift: false
      };
    },
    async confirmCancellation() {
      if (!this.selectedJob) return;

      const { loadingKey, note, repostShift } = this.cancelDialog;
      (this as any).cancelling[loadingKey] = true;

      try {
        let result: any;
        switch (loadingKey) {
          case "byTemp":
            result = await cancelJobHygienist(
              this.selectedJob.hygienistId,
              this.selectedJob.id,
              note
            );
            break;
          case "byDO":
            result = await cancelJobDentist(this.selectedJob.id, note);
            break;
          case "byAgent":
            result = await cancelJobAgent(
              this.selectedJob.id,
              note,
              repostShift
            );
            break;
        }

        const reposted =
          loadingKey === "byAgent" && result && result.reposted === true;
        this.callSnackbar({
          visible: true,
          text: reposted
            ? `Job cancelled ${this.cancelDialog.type} and shift reposted`
            : `Job cancelled ${this.cancelDialog.type} successfully`,
          color: "green darken-2"
        });

        this.cancelDialog.show = false;
        this.showJobDetailDialog = false;
        await this.loadData();
      } catch (error) {
        this.callSnackbar({
          visible: true,
          text:
            error instanceof Error
              ? error.message
              : `Error cancelling job ${this.cancelDialog.type}`,
          color: "red darken-2"
        });
      } finally {
        (this as any).cancelling[loadingKey] = false;
      }
    },

    async changeHourlyRate() {
      try {
        await updateHourlyRate(this.currentItem.id, this.currentHourlyRate);
        await this.loadData();

        this.hourlyRateDialog = false;

        this.callSnackbar({
          visible: true,
          text: "Data updated",
          color: "green darken-2"
        });
      } catch (error) {
        this.callSnackbar({
          visible: true,
          text: "Data not updated",
          color: "red darken-2"
        });
      }
    },
    // T2-6868
    isPaymentMethodEditable,
    formatJobPaymentMethod,
    paymentMethodOptionsFor(item: any) {
      return paymentMethodOpts(item && item.country, item && item.isCentralBooked);
    },
    startPaymentMethodEdit(item: any) {
      if (!item || !isPaymentMethodEditable(item.status)) return;
      if (this.savingPaymentMethodJobId) return;
      this.editingPaymentMethodJobId = item.id;
      this.editingPaymentMethodValue =
        item.paymentMethod === null || item.paymentMethod === undefined
          ? null
          : Number(item.paymentMethod);
    },
    cancelPaymentMethodEdit() {
      if (this.savingPaymentMethodJobId) return;
      this.editingPaymentMethodJobId = null;
      this.editingPaymentMethodValue = null;
    },
    async savePaymentMethod(item: any, nextValue: number) {
      if (!item || this.savingPaymentMethodJobId) return;
      const paymentMethod = Number(nextValue);
      if (!Number.isInteger(paymentMethod)) return;
      if (Number(item.paymentMethod) === paymentMethod) {
        this.cancelPaymentMethodEdit();
        return;
      }
      const previous = item.paymentMethod;
      this.savingPaymentMethodJobId = item.id;
      this.editingPaymentMethodValue = paymentMethod;
      try {
        const result = await updateJobPaymentMethod(item.id, paymentMethod);
        item.paymentMethod = result.paymentMethod;
        this.editingPaymentMethodJobId = null;
        this.editingPaymentMethodValue = null;
        this.callSnackbar({
          visible: true,
          text: `Payment method updated to ${result.paymentMethodLabel}`,
          color: "green darken-2"
        });
      } catch (error) {
        item.paymentMethod = previous;
        this.editingPaymentMethodValue =
          previous === null || previous === undefined ? null : Number(previous);
        this.callSnackbar({
          visible: true,
          text:
            error instanceof Error
              ? error.message
              : "Could not update payment method",
          color: "red darken-2"
        });
        // Status may have changed — refresh so the cell becomes read-only if needed.
        try {
          await this.loadData();
        } catch (_e) {
          /* keep restored local value */
        }
      } finally {
        this.savingPaymentMethodJobId = null;
      }
    },
    updateJobHeaders(headers: any[]) {
      (this as any).managedHeaders = headers;
    },
    handleColumnSnackbar(options: { text: string; color: string }) {
      this.callSnackbar({
        visible: true,
        text: options.text,
        color: options.color
      });
    },
    async loadOffers(jobId: number) {
      this.selectedJobId = jobId;
      this.offersDialog = true;
      this.offersLoading = true;
      this.offersData = [];

      try {
        const offers = await getOffersByJobId(jobId);
        this.offersData = offers;
      } catch (error) {
        this.callSnackbar({
          visible: true,
          text: error instanceof Error ? error.message : "Failed to load offers",
          color: "red darken-2"
        });
      } finally {
        this.offersLoading = false;
      }
    },
    confirmAcceptOffer(offer: any) {
      this.offerToAccept = offer;
      this.acceptOfferDialog = true;
    },
    async executeAcceptOffer() {
      if (!this.offerToAccept) return;
      this.acceptingOfferId = this.offerToAccept.id;
      try {
        await adminAcceptOffer(this.offerToAccept.id);
        this.acceptOfferDialog = false;
        this.callSnackbar({
          visible: true,
          text: `Offer accepted successfully for Job #${this.selectedJobId}`,
          color: "green darken-2"
        });
        if (this.selectedJobId) {
          await this.loadOffers(this.selectedJobId);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to accept offer";
        this.callSnackbar({
          visible: true,
          text: message,
          color: "red darken-2"
        });
      } finally {
        this.acceptingOfferId = null;
        this.offerToAccept = null;
      }
    },
    confirmDeclineOffer(offer: any) {
      this.offerToDecline = offer;
      this.declineReason = "";
      this.declineOfferDialog = true;
    },
    async executeDeclineOffer() {
      if (!this.offerToDecline) return;
      const reason = (this.declineReason || "").trim();
      if (reason.length > 500) {
        this.callSnackbar({
          visible: true,
          text: "Reason must be 500 characters or fewer",
          color: "red darken-2"
        });
        return;
      }
      this.decliningOfferId = this.offerToDecline.id;
      try {
        await adminDeclineOffer(this.offerToDecline.id, reason);
        this.declineOfferDialog = false;
        this.callSnackbar({
          visible: true,
          text: `Offer declined for Job #${this.selectedJobId}`,
          color: "green darken-2"
        });
        if (this.selectedJobId) {
          await this.loadOffers(this.selectedJobId);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to decline offer";
        this.callSnackbar({
          visible: true,
          text: message,
          color: "red darken-2"
        });
      } finally {
        this.decliningOfferId = null;
        this.offerToDecline = null;
        this.declineReason = "";
      }
    },
    confirmRequestResubmit(offer: any) {
      this.offerToResubmit = offer;
      this.requestResubmitDialog = true;
    },
    async executeRequestResubmit() {
      if (!this.offerToResubmit) return;
      this.resubmittingOfferId = this.offerToResubmit.id;
      try {
        await adminRequestResubmit(this.offerToResubmit.id);
        this.requestResubmitDialog = false;
        this.callSnackbar({
          visible: true,
          text: `Resubmit request sent for Job #${this.selectedJobId}`,
          color: "green darken-2"
        });
        if (this.selectedJobId) {
          await this.loadOffers(this.selectedJobId);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to request resubmit";
        this.callSnackbar({
          visible: true,
          text: message,
          color: "red darken-2"
        });
      } finally {
        this.resubmittingOfferId = null;
        this.offerToResubmit = null;
      }
    },
    formatOfferStatus(status: number | null | undefined): string {
      return formatSharedOfferStatus(status);
    },
    getOfferStatusColor(status: number | null | undefined): string {
      if (status === null || status === undefined) return "grey";
      return OFFER_STATUS_COLORS[Number(status)] || "grey";
    }
  }
});
